import type { HelpTopicDetail } from './help-topic-detail.types';
import {
  API_DOC_PATHS,
  API_V1_ROUTE_MODULES,
  API_V2_ROUTE_MODULES,
  MCP_HTTP_ENDPOINTS,
} from '@/lib/api-reference';
function d(
  summary: string,
  o: {
    h?: [string, string][];
    st?: [string, string][];
    se?: { hd: string; p: string[]; b?: string[] }[];
    re?: { label: string; type: 'image' | 'video' | 'page'; pathOrUrl: string; note?: string }[];
    q?: { question: string; options: string[]; correctOptionIndex: number; explanation: string }[];
    t?: string[];
    r?: string[];
  } = {},
): HelpTopicDetail {
  return {
    summary,
    highlights: (o.h ?? []).map(([label, value]) => ({ label, value })),
    steps: (o.st ?? []).map(([title, description]) => ({ title, description })),
    sections: (o.se ?? []).map((x) => ({
      heading: x.hd,
      paragraphs: x.p,
      bullets: x.b,
    })),
    resources: o.re ?? [],
    quiz: o.q ?? [],
    tips: o.t ?? [],
    relatedTopicIds: o.r ?? [],
  };
}

/** Fiches détaillées — contenu rédactionnel aligné sur les fonctionnalités Lyrra. */
export const HELP_TOPIC_DETAILS: Record<string, HelpTopicDetail> = {
  'demarrage-tableau-bord': {
    "summary": "Le tableau de bord est votre page d’accueil d’enseignant. Il permet de créer un cours, de reprendre votre dernier travail et de voir d’un coup d’œil où en sont vos formations et vos élèves.",
    "highlights": [
      {
        "label": "Accès",
        "value": "Menu Pilotage → Accueil"
      },
      {
        "label": "Pour qui",
        "value": "Enseignants et concepteurs"
      },
      {
        "label": "Mise à jour",
        "value": "À chaque ouverture de la page"
      }
    ],
    "steps": [
      {
        "title": "Ouvrir le tableau de bord",
        "description": "Il s’affiche dès la connexion. Depuis une autre page, cliquez « Accueil » en haut du menu de gauche.",
        "slide": 0
      },
      {
        "title": "Créer un cours",
        "description": "Cliquez « Créer une formation », donnez un titre, puis choisissez le mode Simple (chapitres) ou Avancé (parcours visuel).",
        "slide": 1
      },
      {
        "title": "Suivre la publication",
        "description": "Un taux faible signale des brouillons à terminer. Ouvrez-les depuis « Dernières modifications » puis publiez-les.",
        "slide": 2
      },
      {
        "title": "Reprendre un contenu",
        "description": "Cliquez le crayon d’une ligne de « Dernières modifications » pour rouvrir son éditeur.",
        "slide": 4
      },
      {
        "title": "Aller plus loin",
        "description": "Les accès rapides et le menu mènent aux élèves, aux parcours et aux podcasts.",
        "slide": 5
      }
    ],
    "sections": [
      {
        "heading": "Ce que comptent les indicateurs",
        "paragraphs": [
          "Les chiffres portent uniquement sur les contenus dont vous êtes l’auteur."
        ],
        "bullets": [
          "Formations créées : toutes vos formations, brouillons compris.",
          "Apprenants touchés : élèves différents inscrits à au moins une de vos formations.",
          "Taux de publication : formations publiées divisées par formations créées."
        ]
      },
      {
        "heading": "Le tableau de bord est vide ?",
        "paragraphs": [
          "C’est normal tant que vous n’avez créé aucun contenu : commencez par « Créer une formation ». Si les menus Élèves ou Groupes de soutien manquent, demandez à la direction de vérifier que votre compte est bien rattaché à l’établissement."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/demarrage-tableau-bord/01.webp",
        "title": "Vue d’ensemble",
        "caption": "Le tableau de bord s’ouvre après la connexion. À gauche, le menu. Au centre, vos raccourcis et vos indicateurs. À droite, le calendrier d’activité et les accès rapides."
      },
      {
        "src": "/help/screens/fr/demarrage-tableau-bord/02.webp",
        "title": "Créer ou reprendre",
        "caption": "« Créer une formation » ouvre la liste des cours avec la fenêtre de création. « Reprendre la dernière modification » rouvre directement le dernier contenu que vous avez modifié."
      },
      {
        "src": "/help/screens/fr/demarrage-tableau-bord/03.webp",
        "title": "Taux de publication et compteurs",
        "caption": "Le cercle indique la part de vos formations publiées. Dessous : le nombre de formations créées et le nombre d’élèves qui suivent au moins une de vos formations."
      },
      {
        "src": "/help/screens/fr/demarrage-tableau-bord/04.webp",
        "title": "Calendrier d’activité",
        "caption": "Les jours en couleur sont ceux où vous avez modifié une formation, un parcours ou un podcast ce mois-ci. Le jour encadré est aujourd’hui."
      },
      {
        "src": "/help/screens/fr/demarrage-tableau-bord/05.webp",
        "title": "Dernières modifications",
        "caption": "Vos contenus récents, du plus récent au plus ancien, avec leur statut : Brouillon ou Publié. Le crayon ouvre l’éditeur, « Tout voir » ouvre la liste complète."
      },
      {
        "src": "/help/screens/fr/demarrage-tableau-bord/06.webp",
        "title": "Accès rapides",
        "caption": "Raccourcis vers Formations, Parcours, Podcasts, Apprenants et « Nouvelle formation », sans passer par le menu."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Une visite guidée s’affiche à la première connexion. La croix la ferme définitivement.",
      "Le suivi détaillé de vos élèves (progression, classes, groupes) se trouve dans le menu Élèves."
    ],
    "relatedTopicIds": [
      "demarrage-navigation",
      "demarrage-roles",
      "demarrage-formation-simple"
    ]
  },
  'demarrage-navigation': {
    "summary": "Le menu de gauche donne accès à tous vos espaces. La barre du haut regroupe la recherche, les notifications, le thème, la langue et votre compte.",
    "highlights": [
      {
        "label": "Menu",
        "value": "Pilotage, Création, Médias, Aide"
      },
      {
        "label": "Recherche",
        "value": "Ctrl + K (⌘ K sur Mac)"
      },
      {
        "label": "Téléphone",
        "value": "Bouton ☰ en haut de l’écran"
      }
    ],
    "steps": [
      {
        "title": "Repérer votre espace",
        "description": "Sous le logo, le nom de l’espace (Formation, Établissement, Apprenant…) indique où vous êtes.",
        "slide": 0
      },
      {
        "title": "Ouvrir une entrée",
        "description": "Cliquez une entrée du menu : la page s’ouvre et l’entrée reste surlignée.",
        "slide": 1
      },
      {
        "title": "Chercher un contenu",
        "description": "Ctrl + K, un mot, puis Entrée : c’est le chemin le plus court vers une formation.",
        "slide": 2
      },
      {
        "title": "Régler l’affichage",
        "description": "Thème clair ou sombre, langue, menu réduit : ces réglages sont gardés dans ce navigateur.",
        "slide": 3
      },
      {
        "title": "Accéder à votre compte",
        "description": "Cliquez vos initiales en haut de l’écran pour ouvrir votre profil ou vous déconnecter.",
        "slide": 5
      }
    ],
    "sections": [
      {
        "heading": "Pourquoi le menu change d’un compte à l’autre",
        "paragraphs": [
          "Les entrées dépendent de votre rôle et des menus activés pour votre établissement. Un enseignant voit l’espace Formation. La direction voit l’espace Établissement (Classes, Élèves, Évaluations, Groupes, Professeurs…). Un élève voit l’espace Apprenant."
        ],
        "bullets": [
          "Une entrée attendue manque : demandez à la direction de vérifier votre rôle.",
          "Une entrée ne vous concerne pas : la direction peut la masquer pour tout l’établissement."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/demarrage-navigation/01.webp",
        "title": "Le menu latéral",
        "caption": "Le menu est découpé en sections : Pilotage (Accueil, Élèves, Groupes de soutien, Analytiques), Création (Cours, Podcasts, Parcours, Webinaires), Médias et Aide. La page ouverte est surlignée."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/02.webp",
        "title": "Suivre vos élèves",
        "caption": "« Élèves » liste les élèves de vos classes et les inscrits à vos cours, même s’ils n’ont encore rien commencé. « Groupes de soutien » réunit les groupes qui vous sont confiés ou que vous avez créés."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/03.webp",
        "title": "Recherche rapide",
        "caption": "Cliquez « Rechercher… » ou tapez Ctrl + K (⌘ K sur Mac), puis un mot : vos formations, podcasts et parcours s’affichent. Flèches pour se déplacer, Entrée pour ouvrir, Échap pour fermer."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/04.webp",
        "title": "Notifications, thème et langue",
        "caption": "La cloche affiche vos notifications, la lune passe en thème sombre et le globe change la langue de l’interface."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/05.webp",
        "title": "Changer de langue",
        "caption": "Choisissez une langue : l’interface change aussitôt et l’arabe s’affiche de droite à gauche. Les cours restent dans la langue où ils ont été rédigés."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/06.webp",
        "title": "Menu du compte",
        "caption": "Vos initiales ouvrent le menu du compte : Profil, Paramètres, Demande de formation et Déconnexion."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/07.webp",
        "title": "Réduire le menu",
        "caption": "« Réduire le menu », en bas, ne garde que les icônes pour laisser plus de place au contenu. Survolez une icône pour voir son nom, recliquez pour déplier."
      },
      {
        "src": "/help/screens/fr/demarrage-navigation/08.webp",
        "title": "Sur téléphone",
        "caption": "Le menu est replié : touchez ☰ en haut de l’écran pour l’ouvrir, puis choisissez une entrée."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Sur un petit écran, l’éditeur de parcours visuel reste plus confortable sur ordinateur ou tablette.",
      "Le bouton en bas à droite ouvre l’assistant d’aide : posez-lui une question en langage courant."
    ],
    "relatedTopicIds": [
      "demarrage-tableau-bord",
      "demarrage-profil",
      "demarrage-roles"
    ]
  },
  'demarrage-profil': {
    "summary": "Votre profil regroupe vos informations, votre photo, votre mot de passe, vos notifications et la langue de l’interface.",
    "highlights": [
      {
        "label": "Accès",
        "value": "Vos initiales → Profil"
      },
      {
        "label": "Mot de passe",
        "value": "Onglet Sécurité du compte"
      },
      {
        "label": "Langue",
        "value": "Onglet Notifications & langue"
      }
    ],
    "steps": [
      {
        "title": "Ouvrir le profil",
        "description": "Vos initiales, en haut de l’écran, puis « Profil ».",
        "slide": 0
      },
      {
        "title": "Compléter vos informations",
        "description": "Renseignez au moins votre fonction et votre ville : elles aident vos collègues à vous identifier.",
        "slide": 1
      },
      {
        "title": "Ajouter une photo",
        "description": "Cliquez l’appareil photo sur l’avatar et choisissez une image.",
        "slide": 2
      },
      {
        "title": "Sécuriser le compte",
        "description": "Changez le mot de passe reçu par e-mail dès la première connexion.",
        "slide": 3
      },
      {
        "title": "Régler vos préférences",
        "description": "Notifications, langue et fuseau horaire, puis « Enregistrer les préférences ».",
        "slide": 5
      }
    ],
    "sections": [
      {
        "heading": "Mot de passe oublié",
        "paragraphs": [
          "Sur la page de connexion, cliquez « Mot de passe oublié ? » et saisissez votre e-mail : vous recevez un lien pour en choisir un nouveau. Un élève invité par son établissement reçoit un lien pour créer son mot de passe, valable 14 jours."
        ]
      },
      {
        "heading": "Changer d’adresse e-mail",
        "paragraphs": [
          "L’adresse e-mail est votre identifiant. Pour la modifier, contactez la direction de votre établissement."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/demarrage-profil/01.webp",
        "title": "Ouvrir votre profil",
        "caption": "Cliquez vos initiales en haut de l’écran, puis « Profil »."
      },
      {
        "src": "/help/screens/fr/demarrage-profil/02.webp",
        "title": "Informations personnelles",
        "caption": "Modifiez prénom, nom, téléphone, fonction, ville et pays, puis « Enregistrer ». L’e-mail sert à vous connecter : il ne se modifie pas ici. L’établissement de rattachement est rappelé en haut."
      },
      {
        "src": "/help/screens/fr/demarrage-profil/03.webp",
        "title": "Photo de profil",
        "caption": "Le bouton appareil photo, sur l’avatar, ajoute ou remplace votre photo (JPEG, PNG, WebP ou GIF)."
      },
      {
        "src": "/help/screens/fr/demarrage-profil/04.webp",
        "title": "Changer de mot de passe",
        "caption": "Onglet « Sécurité du compte » : saisissez le mot de passe actuel, puis deux fois le nouveau : au moins 8 caractères, avec une majuscule, une minuscule, un chiffre et un caractère spécial. Vos autres appareils sont alors déconnectés."
      },
      {
        "src": "/help/screens/fr/demarrage-profil/05.webp",
        "title": "Notifications",
        "caption": "Onglet « Notifications & langue » : activez ou coupez les e-mails importants, les notifications du navigateur et le rapport hebdomadaire."
      },
      {
        "src": "/help/screens/fr/demarrage-profil/06.webp",
        "title": "Langue et fuseau horaire",
        "caption": "Choisissez la langue de l’interface (11 langues, dont l’arabe) et votre fuseau horaire, puis « Enregistrer les préférences ». La langue s’applique tout de suite."
      },
      {
        "src": "/help/screens/fr/demarrage-profil/07.webp",
        "title": "Paramètres : clés API",
        "caption": "Menu du compte → Paramètres : créez des clés personnelles pour relier un outil externe (MCP ou API). Réservé aux usages techniques."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Sur un appareil partagé, déconnectez-vous toujours depuis le menu du compte.",
      "Le globe de la barre du haut change la langue tout de suite, dans ce navigateur. Le profil l’enregistre en plus avec vos préférences."
    ],
    "relatedTopicIds": [
      "demarrage-navigation",
      "donnees-mots-de-passe",
      "demarrage-roles"
    ]
  },
  'demarrage-formation-simple': d(
    'Le mode Simple permet de créer une formation structurée en modules et chapitres, avec des blocs pédagogiques (texte, vidéo, audio, quiz…) sans utiliser le canvas graphique du designer EduFlow avancé.',
    {
      h: [
        ['Mode Simple', 'Modules → chapitres → blocs'],
        ['Mode Avancé', 'Designer graphe EduFlow (branches, 35+ blocs)'],
        ['Accès', 'Menu Formations → Nouvelle formation'],
      ],
      st: [
        [
          'Ouvrir la liste des formations',
          'Depuis le menu latéral, cliquez sur « Formations » pour accéder à la liste de vos parcours.',
        ],
        [
          'Choisir le mode Simple',
          'Cliquez sur « Nouvelle formation », puis sélectionnez la carte « Simple » (modules, chapitres et blocs vidéo / texte / audio / quiz).',
        ],
        [
          'Donner un titre et créer',
          'Saisissez le titre de la formation et validez : vous arrivez sur la page Structure pour organiser modules et chapitres.',
        ],
        [
          'Composer un chapitre',
          'Ouvrez un chapitre pour accéder à l’éditeur : ajoutez des blocs dans la zone centrale et paramétrez-les dans le panneau de droite.',
        ],
        [
          'Publier et partager',
          'Une fois le contenu prêt, publiez la formation comme pour un parcours classique, puis partagez le lien ou assignez-la aux apprenants.',
        ],
      ],
      se: [
        {
          hd: 'Quand choisir le mode Simple ?',
          p: [
            'Le mode Simple convient aux formations linéaires ou modulaires classiques (cours par chapitres, micro-learning, onboarding).',
            'Préférez le mode Avancé si vous avez besoin de branches conditionnelles, d’un parcours non linéaire ou de blocs spécialisés du designer graphique.',
          ],
        },
        {
          hd: 'Assistant IA',
          p: [
            'Dans la structure ou l’éditeur de chapitre, l’assistant IA peut vous aider à générer ou enrichir le contenu — relisez toujours avant publication.',
          ],
        },
      ],
      t: [
        'Vous pouvez revenir modifier la structure à tout moment tant que la formation n’est pas verrouillée par votre processus interne.',
        'Le player apprenant s’adapte automatiquement au mode Simple une fois la formation publiée.',
      ],
      r: ['demarrage-navigation', 'eduflow-concept', 'pub-statuts'],
    },
  ),

  'demarrage-roles': {
    "summary": "Chaque compte a un rôle, qui fixe ce que la personne voit et ce qu’elle peut faire. Voici les rôles d’un établissement scolaire et d’un réseau d’établissements.",
    "highlights": [
      {
        "label": "Enseignant",
        "value": "Ses cours, ses classes, ses groupes"
      },
      {
        "label": "Direction",
        "value": "Tout l’établissement et l’équipe"
      },
      {
        "label": "Élève",
        "value": "Ses cours, même hors ligne"
      }
    ],
    "steps": [
      {
        "title": "Identifier votre rôle",
        "description": "Le nom affiché sous le logo (Formation, Établissement, Multi-site, Apprenant) indique votre espace.",
        "slide": 0
      },
      {
        "title": "Coordonner les classes",
        "description": "Le professeur principal ouvre Classes pour gérer élèves, enseignants et cours de chaque classe.",
        "slide": 1
      },
      {
        "title": "Piloter l’établissement",
        "description": "La direction retrouve tous les menus de l’établissement et les codes d’inscription.",
        "slide": 2
      },
      {
        "title": "Inviter l’équipe",
        "description": "Professeurs → « Inviter un professeur » : la personne reçoit un e-mail pour activer son compte.",
        "slide": 3
      },
      {
        "title": "Suivre le réseau",
        "description": "Le compte multi-site compare les établissements et en ouvre de nouveaux.",
        "slide": 4
      }
    ],
    "sections": [
      {
        "heading": "Qui peut faire quoi",
        "paragraphs": [
          "Les droits suivent le rôle, pas la personne : changer de rôle change aussitôt les menus."
        ],
        "bullets": [
          "Enseignant : créer et publier ses cours, assigner un cours à ses classes, suivre et noter ses élèves, créer des groupes de soutien.",
          "Professeur principal : tout ce que fait l’enseignant, plus la gestion des classes et des évaluations de l’établissement.",
          "Direction : tout l’établissement, l’équipe pédagogique, les menus affichés et les codes d’inscription.",
          "Multi-site : la vue d’ensemble et la création des établissements du réseau.",
          "Élève : suivre ses cours, ses parcours et sa progression."
        ]
      },
      {
        "heading": "Codes d’inscription",
        "paragraphs": [
          "Sur l’accueil de l’espace Établissement, la direction trouve un code par type de compte : élève, enseignant et direction. Chaque code crée un compte du rôle correspondant : ne communiquez à chacun que le code qui le concerne."
        ]
      },
      {
        "heading": "Comptes entreprise",
        "paragraphs": [
          "Dans une entreprise, on trouve d’autres rôles : DRH et RH, managers et collaborateurs. Ils suivent la même logique, avec des menus adaptés au monde professionnel."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/demarrage-roles/01.webp",
        "title": "Enseignant",
        "caption": "Espace Formation : il crée cours, podcasts et parcours, suit les élèves de ses classes et gère ses groupes de soutien. Il ne voit pas les classes des autres enseignants."
      },
      {
        "src": "/help/screens/fr/demarrage-roles/02.webp",
        "title": "Professeur principal ou coordinateur",
        "caption": "Espace Établissement : il crée les classes, importe les élèves, assigne les cours à une classe et suit les évaluations de tout l’établissement."
      },
      {
        "src": "/help/screens/fr/demarrage-roles/03.webp",
        "title": "Direction de l’établissement",
        "caption": "Espace Établissement complet : contenus, classes, élèves, évaluations, groupes, professeurs, tableaux de bord et codes d’inscription."
      },
      {
        "src": "/help/screens/fr/demarrage-roles/04.webp",
        "title": "Gérer l’équipe",
        "caption": "Menu Professeurs : la direction invite les enseignants et les professeurs principaux, et voit leur rôle, leurs classes et leur dernière activité."
      },
      {
        "src": "/help/screens/fr/demarrage-roles/05.webp",
        "title": "Multi-site (réseau, ministère)",
        "caption": "Espace Multi-site : chiffres consolidés de tous les établissements, création d’établissements, formations partagées et comptes."
      },
      {
        "src": "/help/screens/fr/demarrage-roles/06.webp",
        "title": "Élève",
        "caption": "Espace Apprenant : cours assignés, parcours, progression et documents. Les cours téléchargés restent accessibles sans internet."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Une personne = un compte nominatif : ne partagez pas un compte entre collègues.",
      "Un élève qui s’inscrit seul avec le code « Élève » obtient toujours un compte élève."
    ],
    "relatedTopicIds": [
      "demarrage-navigation",
      "demarrage-tableau-bord",
      "inst-collaborateurs"
    ]
  },
  'eduflow-concept': {
    "summary": "Le concepteur EduFlow sert à construire un cours sous forme de parcours visuel : des blocs (contenus, activités, évaluations) reliés par des flèches qui donnent l’ordre de passage. Tout se fait sur un seul écran, et chaque modification est enregistrée automatiquement.",
    "highlights": [
      {
        "label": "Accès",
        "value": "Formations → formation en mode Avancé"
      },
      {
        "label": "Enregistrement",
        "value": "Automatique, quelques secondes après chaque modification"
      },
      {
        "label": "Pour qui",
        "value": "Enseignants et concepteurs"
      }
    ],
    "steps": [
      {
        "title": "Ouvrir le concepteur",
        "description": "Dans Formations, ouvrez une formation en mode Avancé (parcours visuel), ou créez-en une avec « Créer une formation » puis choisissez « Avancé ».",
        "slide": 0
      },
      {
        "title": "Ajouter un bloc",
        "description": "Glissez un bloc de la palette vers le canevas, ou cliquez-le pour l’ajouter. Reliez-le en tirant une flèche depuis le point de sortie d’un bloc vers le bloc suivant.",
        "slide": 2
      },
      {
        "title": "Remplir le bloc",
        "description": "Cliquez le bloc : renseignez son titre et son contenu dans le panneau Propriétés.",
        "slide": 4
      },
      {
        "title": "Ranger le parcours",
        "description": "Cliquez la baguette « Disposition automatique » pour aligner les blocs proprement.",
        "slide": 5
      },
      {
        "title": "Vérifier comme un élève",
        "description": "Cliquez l’œil « Aperçu » pour parcourir le cours, puis « Fermer l’aperçu ».",
        "slide": 6
      }
    ],
    "sections": [
      {
        "heading": "Raccourcis clavier",
        "paragraphs": [
          "Ils fonctionnent quand le curseur n’est pas dans un champ de texte."
        ],
        "bullets": [
          "Ctrl + Z (⌘ + Z sur Mac) : annuler. Ctrl + Y ou Ctrl + Maj + Z : rétablir.",
          "Ctrl + C puis Ctrl + V : copier-coller les blocs sélectionnés. Ctrl + D : dupliquer.",
          "Ctrl + A : tout sélectionner. Suppr : supprimer la sélection."
        ]
      },
      {
        "heading": "Enregistrement et versions",
        "paragraphs": [
          "À côté du titre, l’indicateur passe de « Non enregistré » à « Enregistré » quelques secondes après chaque modification. L’historique des versions permet de revenir à un état précédent du parcours."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/eduflow-concept/01.webp",
        "title": "Le concepteur ouvert",
        "caption": "À gauche, la palette de blocs. Au centre, le canevas avec le parcours. À droite, le panneau Propriétés, vide tant qu’aucun bloc n’est sélectionné."
      },
      {
        "src": "/help/screens/fr/eduflow-concept/02.webp",
        "title": "La barre d’outils",
        "caption": "Retour, titre et état d’enregistrement à gauche ; annuler, rétablir et zoom ; puis les outils du parcours : paramètres, disposition, validation, assistant IA, versions, objectifs, gamification, variables, traduction, collaboration, export et aperçu."
      },
      {
        "src": "/help/screens/fr/eduflow-concept/03.webp",
        "title": "La palette de blocs",
        "caption": "Les blocs sont rangés par familles : Parcours & structure, Contenu & médias, Activités & évaluation, Accessibilité (DYS), Conformité & avancé. Le champ de recherche filtre la liste."
      },
      {
        "src": "/help/screens/fr/eduflow-concept/04.webp",
        "title": "Le canevas",
        "caption": "Chaque carte est un bloc ; les flèches indiquent l’ordre de passage. En bas : la grille et la minimap, pour se repérer dans les grands parcours."
      },
      {
        "src": "/help/screens/fr/eduflow-concept/05.webp",
        "title": "Le panneau Propriétés",
        "caption": "Un clic sur un bloc ouvre ses réglages : titre, description (lue par la voix off), contenu et options propres au type de bloc. « Supprimer le bloc » se trouve en bas."
      },
      {
        "src": "/help/screens/fr/eduflow-concept/06.webp",
        "title": "Annuler, ranger, prévisualiser",
        "caption": "La flèche circulaire annule la dernière action. La baguette range automatiquement tous les blocs. L’œil, tout à droite, ouvre l’aperçu du cours."
      },
      {
        "src": "/help/screens/fr/eduflow-concept/07.webp",
        "title": "L’aperçu élève",
        "caption": "L’aperçu montre le cours tel que l’élève le verra : sommaire, progression, boutons Précédent / Suivant. « Fermer l’aperçu » ramène au concepteur."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Donnez un titre clair à chaque bloc dès sa création : il apparaît dans le sommaire de l’élève et la validation l’exige.",
      "Lancez l’aperçu souvent : c’est le moyen le plus rapide de repérer un enchaînement qui ne va pas."
    ],
    "relatedTopicIds": [
      "eduflow-blocs",
      "eduflow-validation",
      "demarrage-tableau-bord"
    ]
  },
  'eduflow-blocs': {
    "summary": "Un parcours se construit avec des blocs. Chaque famille a un rôle : structurer le parcours, transmettre le savoir, faire pratiquer et évaluer, ou adapter le cours aux élèves DYS. Chaque bloc se règle dans le panneau Propriétés.",
    "highlights": [
      {
        "label": "Début et fin",
        "value": "Un seul Départ, au moins une Fin"
      },
      {
        "label": "Recherche",
        "value": "Champ « Rechercher un bloc… »"
      },
      {
        "label": "Réglages",
        "value": "Panneau Propriétés, à droite"
      }
    ],
    "steps": [
      {
        "title": "Commencer et finir",
        "description": "Placez un bloc Départ au début et au moins un bloc Fin. Le Départ présente l’objectif de la séance.",
        "slide": 0
      },
      {
        "title": "Apporter le contenu",
        "description": "Ajoutez des blocs de Contenu & médias pour la leçon : texte, vidéo, PDF, carte mentale…",
        "slide": 1
      },
      {
        "title": "Faire pratiquer",
        "description": "Ajoutez un Quiz ou une évaluation après la leçon pour vérifier ce qui est acquis.",
        "slide": 2
      },
      {
        "title": "Trouver un bloc vite",
        "description": "Tapez « quiz », « vidéo » ou « PDF » dans la recherche de la palette.",
        "slide": 4
      },
      {
        "title": "Régler le quiz",
        "description": "Saisissez la question, les réponses, puis choisissez la réponse correcte.",
        "slide": 6
      }
    ],
    "sections": [
      {
        "heading": "Choisir le bon bloc",
        "paragraphs": [],
        "bullets": [
          "Récapitulatif : les points à retenir en fin de séquence.",
          "Sous-formation : insère un autre parcours existant, utile pour réutiliser un module.",
          "Formulaire : recueille des réponses de l’élève, réutilisables ensuite dans d’autres blocs.",
          "Blocs DYS : à proposer aux élèves qui ont des difficultés de lecture, en complément du contenu principal."
        ]
      },
      {
        "heading": "Médias",
        "paragraphs": [
          "Les blocs vidéo, audio, image et PDF puisent dans la bibliothèque de médias. Importez un fichier une seule fois puis réutilisez-le dans plusieurs cours."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/eduflow-blocs/01.webp",
        "title": "Parcours & structure",
        "caption": "Départ, Boucle, Minuteur, Sous-formation, Formulaire, Récapitulatif et Fin : les blocs qui organisent le parcours."
      },
      {
        "src": "/help/screens/fr/eduflow-blocs/02.webp",
        "title": "Contenu & médias",
        "caption": "Texte, audio, image annotée, podcast, vidéo, diaporama, document PDF, carte mentale, frise chronologique, graphique interactif, page web intégrée et visuel IA."
      },
      {
        "src": "/help/screens/fr/eduflow-blocs/03.webp",
        "title": "Activités & évaluation",
        "caption": "Quiz (QCM, vrai / faux, ordre, texte à trous), révélation progressive, auto-évaluation, évaluation vocale et évaluation multi-questions."
      },
      {
        "src": "/help/screens/fr/eduflow-blocs/04.webp",
        "title": "Accessibilité (DYS)",
        "caption": "Exercice de lecture adaptée, glossaire avec quiz intégré, horloge colorée, image à zones interactives et lecteur DYS (syllabes colorées et lecture à voix haute)."
      },
      {
        "src": "/help/screens/fr/eduflow-blocs/05.webp",
        "title": "Rechercher un bloc",
        "caption": "Tapez un mot dans le champ de recherche : seules les familles et les blocs correspondants restent affichés. La croix efface la recherche."
      },
      {
        "src": "/help/screens/fr/eduflow-blocs/06.webp",
        "title": "Régler un bloc Texte",
        "caption": "Bloc sélectionné : titre, description facultative lue par la voix off, puis le contenu mis en forme (gras, listes, liens, tableaux)."
      },
      {
        "src": "/help/screens/fr/eduflow-blocs/07.webp",
        "title": "Régler un bloc Quiz",
        "caption": "Type de quiz, question, quatre options de réponse ; plus bas, la réponse correcte, le mode de branchement et le message affiché après la réponse."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Un bloc Quiz sans réponse correcte déclenche un avertissement dans la validation.",
      "Préférez plusieurs blocs Texte courts à un seul très long : la validation signale les textes trop longs et propose de les découper."
    ],
    "relatedTopicIds": [
      "eval-quiz",
      "medias-bibliotheque",
      "eduflow-branches"
    ]
  },
  'eduflow-branches': {
    "summary": "Une branche est un chemin différent dans le parcours. Un quiz peut envoyer l’élève vers une remédiation s’il se trompe et vers la suite s’il réussit. Un bloc relié à plusieurs blocs propose aussi des voies au choix. Dans les deux cas, toutes les branches doivent se rejoindre avant la fin.",
    "highlights": [
      {
        "label": "Quiz",
        "value": "Sorties Correct / Incorrect ou une par réponse"
      },
      {
        "label": "Voies au choix",
        "value": "L’élève choisit l’ordre"
      },
      {
        "label": "Limite",
        "value": "12 sorties maximum par bloc"
      }
    ],
    "steps": [
      {
        "title": "Choisir le mode",
        "description": "Sélectionnez le quiz puis, dans « Mode de branchement », choisissez Correct / Incorrect.",
        "slide": 1
      },
      {
        "title": "Relier les sorties",
        "description": "Tirez une flèche depuis le point vert vers la suite, et depuis le point rouge vers un bloc de remédiation.",
        "slide": 2
      },
      {
        "title": "Faire converger",
        "description": "Reliez la remédiation au même bloc que la branche « correct » (par exemple « À retenir »).",
        "slide": 0
      },
      {
        "title": "Proposer des voies",
        "description": "Pour laisser le choix, reliez un bloc à plusieurs blocs, puis faites-les tous aboutir au même bloc suivant.",
        "slide": 3
      },
      {
        "title": "Contrôler",
        "description": "Ouvrez « Valider le parcours » : il signale toute branche qui ne rejoint pas les autres.",
        "slide": 5
      }
    ],
    "sections": [
      {
        "heading": "Les règles à respecter",
        "paragraphs": [
          "Elles garantissent qu’aucun élève ne reste bloqué sur une branche sans issue."
        ],
        "bullets": [
          "Toutes les branches issues d’un même bloc doivent atteindre un bloc commun.",
          "Chaque branche doit pouvoir mener à un bloc Fin.",
          "Un bloc ne peut pas avoir plus de 12 flèches sortantes.",
          "Une boucle (retour en arrière) est permise, mais la validation la signale pour que vous la vérifiiez."
        ]
      },
      {
        "heading": "Nommer une flèche",
        "paragraphs": [
          "Double-cliquez une flèche pour lui donner un nom et une couleur (par exemple « Réussi » ou « Branche A ») : le parcours devient plus lisible pour vos collègues."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/eduflow-branches/01.webp",
        "title": "Une remédiation après le quiz",
        "caption": "Exemple : après « Vérifier ses acquis », l’élève qui se trompe passe par « Remédiation » ; celui qui réussit va directement à « À retenir ». Les deux chemins se rejoignent."
      },
      {
        "src": "/help/screens/fr/eduflow-branches/02.webp",
        "title": "Le mode de branchement",
        "caption": "Dans les propriétés du quiz : Aucun (une seule sortie), Correct / Incorrect (deux sorties) ou Par réponse individuelle (une sortie par réponse A à D)."
      },
      {
        "src": "/help/screens/fr/eduflow-branches/03.webp",
        "title": "Les sorties du quiz",
        "caption": "En mode Correct / Incorrect, le bloc quiz a deux points de sortie : vert pour une bonne réponse, rouge pour une erreur. Tirez une flèche depuis chacun."
      },
      {
        "src": "/help/screens/fr/eduflow-branches/04.webp",
        "title": "Des voies au choix",
        "caption": "Ici, « Thèse, argument, exemple » est relié à deux blocs : une vidéo et une fiche méthode. Les deux voies se rejoignent sur « Entraînement »."
      },
      {
        "src": "/help/screens/fr/eduflow-branches/05.webp",
        "title": "Ce que voit l’élève",
        "caption": "Dans le cours, l’élève choisit sa prochaine voie. Les voies terminées restent accessibles pour les revoir ; il doit parcourir chacune avant la suite commune."
      },
      {
        "src": "/help/screens/fr/eduflow-branches/06.webp",
        "title": "Une branche qui ne rejoint pas",
        "caption": "Si une branche s’arrête sans rejoindre les autres, la validation l’indique : « Les branches depuis … ne convergent pas vers un bloc commun »."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Quand vous changez le mode de branchement, les flèches existantes sont reportées au mieux sur les nouvelles sorties : vérifiez-les ensuite.",
      "Testez chaque branche dans l’aperçu en répondant une fois juste, une fois faux."
    ],
    "relatedTopicIds": [
      "eduflow-validation",
      "eduflow-blocs",
      "eval-quiz"
    ]
  },
  'eduflow-validation': {
    "summary": "Le bouton « Valider le parcours » contrôle tout le schéma en une fois. Les erreurs empêchent la publication ; les avertissements signalent un point à vérifier. Un clic sur un message sélectionne le bloc concerné.",
    "highlights": [
      {
        "label": "Erreurs",
        "value": "Bloquent la publication"
      },
      {
        "label": "Avertissements",
        "value": "À relire, non bloquants"
      },
      {
        "label": "Correction",
        "value": "Clic sur un message → bloc sélectionné"
      }
    ],
    "steps": [
      {
        "title": "Lancer la validation",
        "description": "Cliquez le bouclier « Valider le parcours » dans la barre d’outils.",
        "slide": 0
      },
      {
        "title": "Lire les messages",
        "description": "Les pastilles rouges sont des erreurs, les orange des avertissements.",
        "slide": 1
      },
      {
        "title": "Corriger",
        "description": "Cliquez un message : le bloc est sélectionné. Corrigez-le dans le panneau Propriétés.",
        "slide": 2
      },
      {
        "title": "Relancer",
        "description": "Cliquez de nouveau « Valider le parcours » jusqu’à obtenir « Valide ».",
        "slide": 3
      }
    ],
    "sections": [
      {
        "heading": "Erreurs (bloquantes)",
        "paragraphs": [],
        "bullets": [
          "Aucun ou plusieurs blocs Départ ; aucun bloc Fin.",
          "Bloc sans titre, ou bloc sans flèche vers la suite (sauf la Fin).",
          "Branche qui ne mène à aucune Fin, ou branches qui ne se rejoignent pas.",
          "Plus de 12 flèches sortantes sur un même bloc.",
          "Champ de formulaire utilisé avant que l’élève ait rempli le formulaire, ou champ qui n’existe plus."
        ]
      },
      {
        "heading": "Avertissements (à vérifier)",
        "paragraphs": [],
        "bullets": [
          "Bloc isolé du reste du parcours ; bloc Fin qui a encore une flèche sortante.",
          "Boucle détectée dans le parcours.",
          "Bloc Texte vide, ou texte trop long : le bouton « Séparer » propose de le répartir en plusieurs blocs.",
          "Quiz QCM sans réponse correcte choisie."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/eduflow-validation/01.webp",
        "title": "Le bouton Valider",
        "caption": "Le bouclier de la barre d’outils ouvre le panneau Validation. Ici, un brouillon de cours sur le théorème de Pythagore, encore incomplet."
      },
      {
        "src": "/help/screens/fr/eduflow-validation/02.webp",
        "title": "Le panneau Validation",
        "caption": "En haut, l’état « Invalide » et le nombre d’erreurs et d’avertissements. Dessous, un message par problème, puis le nombre de blocs et de liaisons du parcours."
      },
      {
        "src": "/help/screens/fr/eduflow-validation/03.webp",
        "title": "Aller au bloc concerné",
        "caption": "Un clic sur « Le bloc « Texte » doit avoir un titre » sélectionne le bloc fautif et ouvre ses propriétés : il ne reste qu’à saisir le titre et à le relier à la suite."
      },
      {
        "src": "/help/screens/fr/eduflow-validation/04.webp",
        "title": "Un parcours valide",
        "caption": "Quand tout est corrigé, le panneau affiche « Valide », 0 erreur et 0 avertissement. Le cours peut être publié."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Gardez le panneau ouvert pendant que vous restructurez : il se met à jour à chaque nouvelle validation.",
      "Zéro erreur ne suffit pas : faites aussi un aperçu complet avant de publier."
    ],
    "relatedTopicIds": [
      "eduflow-branches",
      "pub-statuts",
      "eduflow-concept"
    ]
  },
  'eduflow-objectifs': {
    "summary": "Le panneau Objectifs pédagogiques liste ce que l’élève doit savoir faire à la fin du cours. Chaque objectif est relié aux blocs qui le travaillent : un bloc à terminer, ou un quiz à réussir avec un score minimum. Le lecteur affiche ces objectifs à l’élève.",
    "highlights": [
      {
        "label": "Accès",
        "value": "Bouton cible de la barre d’outils"
      },
      {
        "label": "Classement",
        "value": "Catégorie et niveau de Bloom"
      },
      {
        "label": "Preuve",
        "value": "Bloc terminé ou score minimum"
      }
    ],
    "steps": [
      {
        "title": "Ouvrir le panneau",
        "description": "Cliquez la cible « Objectifs pédagogiques » dans la barre d’outils.",
        "slide": 0
      },
      {
        "title": "Écrire l’objectif",
        "description": "Complétez « L’élève sera capable de… » avec un verbe observable : identifier, calculer, rédiger…",
        "slide": 2
      },
      {
        "title": "Le classer",
        "description": "Choisissez la catégorie (savoir, savoir-faire, savoir-être) et le niveau de Bloom.",
        "slide": 2
      },
      {
        "title": "Relier les blocs",
        "description": "Dans « Blocs liés », activez la leçon et l’exercice correspondants, puis cliquez « Ajouter ».",
        "slide": 3
      },
      {
        "title": "Relire la liste",
        "description": "Cliquez un objectif pour le modifier ; la corbeille, au survol, le supprime.",
        "slide": 1
      }
    ],
    "sections": [
      {
        "heading": "Catégories et niveaux",
        "paragraphs": [],
        "bullets": [
          "Savoir : des connaissances (définitions, règles, dates).",
          "Savoir-faire : des méthodes et des gestes à maîtriser.",
          "Savoir-être : des attitudes (coopérer, argumenter avec respect).",
          "Niveaux de Bloom, du plus simple au plus exigeant : connaissance, compréhension, application, analyse, synthèse, évaluation."
        ]
      },
      {
        "heading": "Enregistrement",
        "paragraphs": [
          "Les objectifs sont enregistrés automatiquement une seconde après chaque modification. Le bouton « Sauvegarder » force l’enregistrement immédiat."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/eduflow-objectifs/01.webp",
        "title": "Le bouton Objectifs",
        "caption": "La cible de la barre d’outils ouvre le panneau Objectifs pédagogiques."
      },
      {
        "src": "/help/screens/fr/eduflow-objectifs/02.webp",
        "title": "La liste des objectifs",
        "caption": "En haut, le formulaire d’ajout. Dessous, les objectifs déjà définis, avec leur catégorie, leur niveau, la mention Obligatoire et le nombre de blocs liés."
      },
      {
        "src": "/help/screens/fr/eduflow-objectifs/03.webp",
        "title": "Ajouter un objectif",
        "caption": "Écrivez l’objectif en commençant par un verbe d’action (« Placer une fraction sur une droite graduée »), choisissez la catégorie, le niveau de Bloom et, si besoin, Obligatoire."
      },
      {
        "src": "/help/screens/fr/eduflow-objectifs/04.webp",
        "title": "Relier des blocs",
        "caption": "Ouvrez « Blocs liés » et activez les blocs qui travaillent l’objectif. Pour chacun, choisissez « Complété » ou « Score ≥ » avec un pourcentage, puis cliquez « Ajouter »."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Trois à cinq objectifs suffisent pour une séance : au-delà, ils deviennent difficiles à suivre pour l’élève.",
      "Reliez chaque objectif obligatoire à au moins un quiz ou une évaluation : c’est la preuve qu’il est atteint."
    ],
    "relatedTopicIds": [
      "eduflow-ia",
      "qualite-tracabilite",
      "app-progression"
    ]
  },
  'eduflow-ia': {
    "summary": "L’assistant IA travaille sur votre parcours : il peut rédiger un contenu, proposer un exercice, auditer l’enchaînement ou modifier le bloc ouvert. Il propose ses modifications, et vous choisissez de les appliquer ou non. Dans un quiz, un bouton génère aussi une question à partir des blocs qui précèdent.",
    "highlights": [
      {
        "label": "Accès",
        "value": "Bouton robot de la barre d’outils"
      },
      {
        "label": "Cible",
        "value": "Le bloc ouvert dans Propriétés"
      },
      {
        "label": "Contrôle",
        "value": "Rien n’est modifié sans votre accord"
      }
    ],
    "steps": [
      {
        "title": "Ouvrir l’assistant",
        "description": "Cliquez le robot « Assistant IA » dans la barre d’outils.",
        "slide": 0
      },
      {
        "title": "Choisir la cible",
        "description": "Ouvrez le bloc à travailler dans Propriétés : l’assistant le prend comme cible.",
        "slide": 2
      },
      {
        "title": "Écrire une demande précise",
        "description": "Indiquez le sujet, le niveau et le format attendu : « 3 questions de QCM sur les fractions, niveau 6e ».",
        "slide": 2
      },
      {
        "title": "Relire avant d’appliquer",
        "description": "Gardez « Demander avant d’agir » activé : chaque modification proposée attend votre accord.",
        "slide": 3
      },
      {
        "title": "Générer un QCM depuis la leçon",
        "description": "Dans un quiz, cliquez le bouton orange, cochez les blocs, générez, relisez, puis appliquez.",
        "slide": 5
      }
    ],
    "sections": [
      {
        "heading": "Donner du contexte",
        "paragraphs": [
          "Plus l’assistant a de matière, meilleure est la réponse."
        ],
        "bullets": [
          "Le viseur capture l’écran (canevas et formulaire) pour que l’IA voie ce que vous voyez.",
          "Le trombone joint un PDF, un document Word ou une présentation.",
          "Les options acceptent jusqu’à 3 adresses de pages web comme sources.",
          "Vous pouvez aussi coller une image avec Ctrl + V."
        ]
      },
      {
        "heading": "Votre responsabilité",
        "paragraphs": [
          "L’IA peut se tromper : vérifiez l’exactitude, le niveau de langue et l’adéquation au programme avant de publier. Ne collez pas de données personnelles d’élèves dans la conversation."
        ]
      }
    ],
    "screenshots": [
      {
        "src": "/help/screens/fr/eduflow-ia/01.webp",
        "title": "Le bouton Assistant IA",
        "caption": "Le robot de la barre d’outils ouvre le panneau de l’assistant, à droite du canevas."
      },
      {
        "src": "/help/screens/fr/eduflow-ia/02.webp",
        "title": "Le panneau de l’assistant",
        "caption": "En haut, l’historique, une nouvelle conversation et la cible. Au centre, la conversation. En bas, les options, « Demander avant d’agir », la capture d’écran, la pièce jointe et le champ de message."
      },
      {
        "src": "/help/screens/fr/eduflow-ia/03.webp",
        "title": "Cibler un bloc",
        "caption": "Avec le quiz « Vérifier ses acquis » ouvert dans Propriétés, la cible devient ce bloc. Il suffit d’écrire la demande, par exemple : « ajoute une question sur la comparaison de 2/5 et 4/5, avec un feedback »."
      },
      {
        "src": "/help/screens/fr/eduflow-ia/04.webp",
        "title": "Les options",
        "caption": "Réponse en direct (streaming), application automatique au parcours, contrôles complémentaires (audit du parcours, schéma QCM strict), idées prêtes à l’emploi et jusqu’à 3 pages web comme sources."
      },
      {
        "src": "/help/screens/fr/eduflow-ia/05.webp",
        "title": "Générer un QCM",
        "caption": "Dans les propriétés d’un quiz, le bouton orange à côté de « Question » crée une question à partir des blocs placés avant le quiz."
      },
      {
        "src": "/help/screens/fr/eduflow-ia/06.webp",
        "title": "La fenêtre QCM par IA",
        "caption": "Cochez les blocs à utiliser, cliquez « Générer la question », relisez l’aperçu (4 réponses, 1 correcte), puis « Appliquer au quiz »."
      }
    ],
    "resources": [],
    "quiz": [],
    "tips": [
      "Une demande vague (« améliore le cours ») donne une réponse vague : nommez le bloc, l’objectif et le format.",
      "L’historique des versions permet de revenir en arrière après une modification par l’IA."
    ],
    "relatedTopicIds": [
      "eduflow-objectifs",
      "eduflow-validation",
      "eval-quiz"
    ]
  },
  'pub-statuts': d(
    'Un parcours EduFlow traverse des statuts (brouillon, publié selon votre workflow). Publier rend le contenu disponible pour les apprenants autorisés ; les modifications ultérieures peuvent nécessiter une republication ou une mise à jour versionnée.',
    {
      h: [
        ['Brouillon', 'Édition sans impact apprenant'],
        ['Publié', 'Player à jour pour nouveaux accès'],
        ['Versions', 'Historique si activé'],
      ],
      st: [
        ['Tester avant diffusion', 'Utilisez la prévisualisation ou un compte test pour valider médias et navigation.'],
        ['Enregistrer', 'Sauvegardez avant de changer de statut ; les gros parcours peuvent prendre quelques secondes.'],
        ['Communiquer', 'Prévenez les formateurs ou RH lors d’une republication majeure (contenu certifiant).'],
      ],
      se: [
        {
          hd: 'Impact sur les apprenants en cours',
          p: [
            'Selon la configuration, les apprenants déjà engagés peuvent conserver une version ou basculer sur la nouvelle — clarifiez la règle en interne.',
          ],
          b: ['Documentez la date de version dans votre LMS ou intranet si vous dupliquez l’information.'],
        },
      ],
      t: ['Évitez de publier un vendredi soir sans garde : en cas de régression, personne ne pourra corriger.'],
      r: ['pub-liens', 'eduflow-validation'],
    },
  ),

  'pub-liens': d(
    'Lyrra génère des liens d’accès pour apprenants (souvent avec jeton ou code), parfois des URL présentateur ou de prévisualisation pour les concepteurs.',
    {
      h: [
        ['Apprenant', 'Lien personnel ou générique'],
        ['Sécurité', 'Ne pas exposer sur le web public'],
        ['Expiration', 'Selon politique institution'],
      ],
      st: [
        ['Générer le lien', 'Depuis la fiche formation ou la section partage, créez le lien ; copiez-le dans votre LMS, e-mail ou QR code.'],
        ['Restreindre l’accès', 'Associez le parcours à un groupe ou une campagne si votre organisation impose un périmètre.'],
        ['Révoquer', 'En cas de fuite, régénérez les jetons ou désactivez l’accès côté administration.'],
      ],
      se: [{ hd: 'Conformité', p: ['Les liens font partie des données d’accès : traçabilité et durée de vie doivent être documentées pour le RGPD.'] }],
      t: ['Ne postez jamais un lien apprenant sur un réseau social ouvert sans anonymisation prévue.'],
      r: ['app-inscriptions', 'donnees-rgpd'],
    },
  ),

  'pub-catalogue': d(
    'Le catalogue institutionnel regroupe les offres visibles selon les règles de votre établissement (formation interne, vente, catalogue public).',
    {
      h: [
        ['Visibilité', 'Par rôle / groupe'],
        ['Métadonnées', 'Titre, durée, public'],
        ['Accès', 'Inscription ou lien selon config'],
      ],
      st: [
        ['Publier dans le catalogue', 'Renseignez les champs obligatoires (titre, description courte, vignette) pour un affichage cohérent.'],
        ['Aligner avec la communication', 'Les textes catalogue sont souvent la première impression — harmonisez avec votre site vitrine.'],
        ['Mesurer', 'Les analytiques ou inscriptions permettent de suivre l’intérêt pour chaque offre.'],
      ],
      se: [{ hd: 'Multi-offres', p: ['Si plusieurs parcours se chevauchent, clarifiez le positionnement (niveau, prérequis) pour éviter les mauvaises inscriptions.'] }],
      t: ['Mettez à jour le catalogue quand un parcours est retiré ou remplacé pour ne pas inscrire sur une ancienne version.'],
      r: ['marque-landing', 'pub-liens'],
    },
  ),

  'app-inscriptions': d(
    'La liste des apprenants (ou participants) sur un EduFlow regroupe e-mails, noms, progression, dernière activité et parfois les sessions de blocs détaillées.',
    {
      h: [
        ['Vue liste', 'Recherche & filtres'],
        ['Données', 'Progression JSON + temps'],
        ['Export', 'Selon permissions'],
      ],
      st: [
        ['Ouvrir la liste', 'Depuis le menu Formations ou la fiche parcours, accédez aux apprenants assignés.'],
        ['Filtrer', 'Utilisez la recherche par e-mail ou nom pour les gros effectifs.'],
        ['Analyser une ligne', 'Cliquez pour voir le détail : blocs visités, scores, certificats éventuels.'],
      ],
      se: [{ hd: 'RGPD', p: ['Les données sont personnelles : limitez l’export aux personnes habilitées et documentez la finalité.'] }],
      t: ['Pour les sessions présentielles, croisez la liste Lyrra avec votre feuille d’émargence officielle.'],
      r: ['app-progression', 'analytics-indicateurs'],
    },
  ),

  'app-groupes': d(
    'Les groupes (learning groups) servent à regrouper des apprenants pour des campagnes, du reporting ou des partages de contenu au niveau institution.',
    {
      h: [
        ['Création', 'Admin / RH'],
        ['Membres', 'Ajout manuel ou import'],
        ['Usage', 'Ciblage campagnes'],
      ],
      st: [
        ['Constituer un groupe', 'Créez le groupe dans l’espace institution puis ajoutez les membres ou synchronisez si un connecteur existe.'],
        ['Associer à une action', 'Sélectionnez le groupe lors du déploiement d’un parcours ou d’une communication.'],
        ['Maintenir', 'Mettez à jour les membres en fin de contrat ou de promotion pour des indicateurs justes.'],
      ],
      se: [{ hd: 'Données', p: ['Les effectifs de groupe peuvent apparaître dans les tableaux de bord RH — vérifiez les règles de confidentialité internes.'] }],
      t: ['Nommez les groupes de façon stable (« Promo 2025 A ») plutôt que des noms de personnes seules.'],
      r: ['inst-structure', 'app-inscriptions'],
    },
  ),

  'app-progression': d(
    'La progression stocke pour chaque inscription les blocs visités ou complétés, le temps passé, les scores et les dates de début / fin.',
    {
      h: [
        ['Complétion', 'Booléen + pourcentage estimé'],
        ['Blocs', 'Sessions par bloc'],
        ['Temps', 'Agrégé côté analytiques'],
      ],
      st: [
        ['Lire le statut', '« En cours », « Terminé », « Non commencé » dépendent des événements enregistrés par le player.'],
        ['Interpréter le temps', 'Le temps par bloc peut exclure les pauses lecteur fermé — utilisez-le comme ordre de grandeur.'],
        ['Agir sur les abandons', 'Identifiez les apprenants sans activité depuis X jours pour relances pédagogiques.'],
      ],
      se: [{ hd: 'Limites', p: ['Une progression à 100 % sans évaluation ne garantit pas la maîtrise — croisez avec quiz ou contrôle continu.'] }],
      t: ['Les correctifs de bugs player peuvent recalculer certaines métriques après déploiement.'],
      r: ['analytics-vue', 'eval-quiz'],
    },
  ),

  'app-certificats': d(
    'Les certificats attestent la réussite d’un parcours lorsque les conditions (blocs obligatoires, scores, Fin atteinte) sont remplies.',
    {
      h: [
        ['Génération', 'Automatique ou manuelle selon config'],
        ['Preuve', 'PDF / téléchargement'],
        ['Traçabilité', 'Associée à l’inscription'],
      ],
      st: [
        ['Vérifier les critères', 'Dans le designer, assurez-vous que la Fin est joignable et que les évaluations ont des seuils clairs.'],
        ['Contrôler les délivrances', 'L’interface liste les certificats émis ; en cas de litige, conservez l’historique.'],
        ['Communiquer la valeur', 'Précisez auprès des apprenants si le certificat vaut au titre d’une certification professionnelle ou d’une attestation interne.'],
      ],
      se: [{ hd: 'Qualiopi', p: ['Conservez la preuve d’évaluation et d’assiduité en complément du certificat pour le dossier qualité.'] }],
      t: ['En cas de changement de nom de formation, les certificats déjà émis conservent souvent l’ancien intitulé — anticipez avant campagne.'],
      r: ['qualite-tracabilite', 'pub-statuts'],
    },
  ),

  'medias-bibliotheque': d(
    'La bibliothèque média centralise fichiers audio, vidéo, images et documents pour les réutiliser dans les blocs EduFlow sans re-upload systématique.',
    {
      h: [
        ['Import', 'Glisser-déposer ou bouton'],
        ['Formats', 'MP4, MP3, PNG, PDF…'],
        ['Réutilisation', 'Référence dans plusieurs blocs'],
      ],
      st: [
        ['Importer', 'Choisissez un fichier respectant la taille max configurée sur l’instance ; renommez avant import pour faciliter la recherche.'],
        ['Organiser', 'Utilisez des préfixes ou dossiers logiques si l’interface le permet ; sinon convention de nommage d’équipe.'],
        ['Insérer', 'Dans un bloc, sélectionnez « depuis la bibliothèque » plutôt qu’un lien externe fragile.'],
      ],
      se: [{ hd: 'Stockage', p: ['Les médias lourds impactent la sauvegarde et le player : compressez vidéo/audio quand c’est possible.'] }],
      t: ['Vérifiez les droits d’utilisation (musique, images stock) avant publication publique.'],
      r: ['medias-video', 'eduflow-blocs'],
    },
  ),

  'medias-studio-audio': d(
    'Le studio audio permet d’enregistrer, d’éditer légèrement ou de générer de la voix (TTS) pour des contenus pédagogiques.',
    {
      h: [
        ['Pistes', 'Multi-pistes selon UI'],
        ['TTS', 'Voix et paramètres'],
        ['Export', 'Vers bibliothèque ou bloc'],
      ],
      st: [
        ['Préparer le script', 'Écrire un texte ponctué pour la synthèse vocale ; évitez les abréviations ambiguës.'],
        ['Enregistrer', 'Utilisez un micro correct et un environnement calme ; niveau son homogène entre les leçons.'],
        ['Contrôler l’écoute', 'Réécoutez intégralement avant publication — le TTS peut mal prononcer noms propres.'],
      ],
      se: [{ hd: 'Accessibilité', p: ['Proposez une version texte équivalente pour les contenus uniquement audio.'] }],
      t: ['Normalisez le volume entre les chapitres pour éviter les surprises enchaînées dans le player.'],
      r: ['medias-bibliotheque', 'marque-accessibilite'],
    },
  ),

  'medias-video': d(
    'Les vidéos intégrées dans Lyrra passent par upload ou URL selon le bloc ; le player HTML5 gère lecture, sous-titres si fournis.',
    {
      h: [
        ['Codec', 'H.264 + AAC fréquent'],
        ['Sous-titres', 'Fichier VTT recommandé'],
        ['Poids', 'Streaming & mobile'],
      ],
      st: [
        ['Encoder', 'Export 720p ou 1080p raisonnable ; bitrate modéré pour les réseaux mobiles des apprenants.'],
        ['Ajouter sous-titres', 'Indispensable pour l’accessibilité et les environnements sonores bruyants.'],
        ['Tester', 'Lecture sur Chrome / Safari mobile avant lancement de cohorte.'],
      ],
      se: [{ hd: 'Hébergement externe', p: ['Si vous pointez vers YouTube/Vimeo, respectez la politique de confidentialité (cookies, tracking) de votre organisme.'] }],
      t: ['Une vidéo de plus de 15 minutes sans découpage peut faire chuter l’engagement — découpez en blocs.'],
      r: ['medias-bibliotheque', 'eduflow-blocs'],
    },
  ),

  'medias-documents': d(
    'Les PDF et documents téléchargeables complètent le parcours : supports de cours, fiches mémo, modèles à remplir.',
    {
      h: [
        ['PDF', 'Visionneuse intégrée ou download'],
        ['Accessibilité', 'PDF balisé de préférence'],
        ['Version', 'Numéro dans le nom de fichier'],
      ],
      st: [
        ['Préparer le fichier', 'Réduisez la taille, vérifiez l’orientation et la lisibilité mobile.'],
        ['Joindre au bloc', 'Utilisez le champ document du bloc concerné ; testez le téléchargement en navigation privée.'],
        ['Mettre à jour', 'Lors d’un changement de version, remplacez le fichier et informez les apprenants en cours si nécessaire.'],
      ],
      se: [{ hd: 'Propriété intellectuelle', p: ['Ne diffusez que des documents pour lesquels vous avez les droits de reproduction.'] }],
      t: ['Nommez « v2_2025_03_module3.pdf » pour éviter confusion avec d’anciennes versions.'],
      r: ['marque-accessibilite', 'eduflow-blocs'],
    },
  ),

  'podcasts-creation': d(
    'Le module podcast permet de structurer des épisodes long format, métadonnées et visuels de couverture, distincts du découpage bloc par bloc du EduFlow.',
    {
      h: [
        ['Épisodes', 'Titre, durée, ordre'],
        ['Audio', 'Fichier source haute qualité'],
        ['Image', 'Cover carré recommandé'],
      ],
      st: [
        ['Créer l’émission', 'Définissez titre, description et langue ; cohérent avec votre charte éditoriale.'],
        ['Ajouter épisodes', 'Uploadez l’audio final ; vérifiez loudness -16 LUFS approximatif pour confort d’écoute.'],
        ['Publier', 'Passez le statut adéquat pour rendre disponible aux abonnés ou groupe cible.'],
      ],
      se: [{ hd: 'Différence avec EduFlow', p: ['Un podcast sert à du linéaire audio ; un EduFlow sert à un parcours interactif avec évaluations.'] }],
      t: ['Ajoutez des chapitres ou titres d’épisode explicites pour la recherche dans les apps de podcast.'],
      r: ['podcasts-diffusion', 'medias-studio-audio'],
    },
  ),

  'podcasts-diffusion': d(
    'La diffusion contrôle qui peut écouter (institution, public, lien) et alimente les statistiques d’écoute lorsqu’elles sont exposées.',
    {
      h: [
        ['Cible', 'Interne / externe'],
        ['Partage', 'Lien ou intégration'],
        ['Mesure', 'Écoutes agrégées'],
      ],
      st: [
        ['Choisir le périmètre', 'Restreignez aux membres de l’établissement pour du contenu confidentiel.'],
        ['Communiquer', 'Envoyez le lien officiel ; évitez les copies de fichiers MP3 non contrôlées pour du contenu payant.'],
        ['Analyser', 'Utilisez les stats pour décider des sujets de saisons suivantes.'],
      ],
      se: [{ hd: 'Archivage', p: ['Les podcasts supprimés peuvent passer par la corbeille institution — vérifiez la procédure de restauration.'] }],
      t: ['Pour un podcast RH interne, synchronisez la sortie avec la communication managers.'],
      r: ['inst-trash', 'analytics-indicateurs'],
    },
  ),

  'eval-quiz': d(
    'Les quiz à choix multiples permettent feedback immédiat, barème, nombre de tentatives et parfois branchement selon score.',
    {
      h: [
        ['Questions', 'Énoncé + distracteurs'],
        ['Barème', 'Points par question'],
        ['Feedback', 'Juste / faux pédagogique'],
      ],
      st: [
        ['Rédiger', 'Une seule bonne réponse claire ; distracteurs plausibles mais non ambigus.'],
        ['Configurer la notation', 'Renseignez le scoring dans le bloc pour lever l’avertissement de validation.'],
        ['Tester', 'Passez le quiz en mode apprenant avec les trois profils : bon, moyen, mauvais.'],
      ],
      se: [{ hd: 'Anti-triche', p: ['Limitez les tentatives et randomisez l’ordre des questions si le player l’offre.'] }],
      t: ['Évitez le double négatif dans les énoncés — source fréquente de contestation.'],
      r: ['eval-ouverte', 'eduflow-validation'],
    },
  ),

  'eval-ouverte': d(
    'Les questions ouvertes collectent des réponses textuelles ; la correction est souvent manuelle ou semi-assistée.',
    {
      h: [
        ['Consigne', 'Critères d’évaluation explicites'],
        ['Correction', 'Charge formateur'],
        ['Traçabilité', 'Historique des réponses'],
      ],
      st: [
        ['Rédiger la question', 'Indiquez longueur attendue, exemple de réponse complète et grille de notation interne.'],
        ['Prévoir la charge', 'Pour 200 apprenants, prévoyez un outillage ou une équipe de correcteurs.'],
        ['Rendre les copies', 'Utilisez les commentaires dans l’outil ou exportez pour correction externe selon process.'],
      ],
      se: [{ hd: 'RGPD', p: ['Les réponses libres peuvent contenir des données sensibles — limitez la visibilité.'] }],
      t: ['Pour l’oral simulé, préférez un enregistrement audio dédié avec consentement explicite.'],
      r: ['eval-quiz', 'app-inscriptions'],
    },
  ),

  'eval-auto-eval': d(
    'L’auto-évaluation et la remédiation invitent l’apprenant à se positionner puis à consommer des ressources ciblées selon le résultat.',
    {
      h: [
        ['Positionnement', 'Grille ou échelle'],
        ['Ressources', 'Liens conditionnels'],
        ['Pédagogie', 'Métacognition'],
      ],
      st: [
        ['Concevoir la grille', 'Alignez les critères sur les objectifs du module.'],
        ['Brancher le parcours', 'Utilisez les sorties multiples pour diriger vers remédiation ou approfondissement.'],
        ['Mesurer l’effet', 'Comparez scores avant/après sur un quiz sommatif.'],
      ],
      se: [{ hd: 'Sincérité', p: ['L’auto-évaluation seule ne suffit pas à certifier — combinez avec une évaluation externe pour les diplômes.'] }],
      t: ['Affichez un message rassurant : l’auto-évaluation est un outil de progrès, pas un piège.'],
      r: ['eduflow-branches', 'eduflow-objectifs'],
    },
  ),

  'analytics-vue': d(
    'La page Analytiques offre des graphiques personnalisables (ligne, barres, camembert, aires), filtres par période et par formation, disposition sauvegardée localement.',
    {
      h: [
        ['Filtres', '7j à 12 mois, parcours'],
        ['Données', 'Issues de la base (inscriptions, sessions)'],
        ['Layout', 'localStorage navigateur'],
      ],
      st: [
        ['Choisir la période', 'Les courtes périodes pour le pilotage tactique ; 90 jours ou 12 mois pour les tendances.'],
        ['Filtrer une formation', '« Toutes les formations » agrège vos parcours accessibles ; une formation isolée affine le diagnostic.'],
        ['Réorganiser', 'Glissez les cartes, changez la taille S/M/L ; ajoutez ou retirez des graphiques selon votre besoin de reporting.'],
      ],
      se: [{ hd: 'Interprétation', p: ['Un pic de complétions peut correspondre à une relance RH — croisez toujours avec le contexte métier.'] }],
      t: ['Réinitialisez la disposition si après une mise à jour l’affichage semble incohérent.'],
      r: ['analytics-indicateurs', 'app-progression'],
    },
  ),

  'analytics-indicateurs': d(
    'Indicateurs typiques : taux de complétion moyen par fenêtre temporelle, répartition statuts (terminé / en cours / non commencé), temps d’engagement sur blocs, apprenants distincts par bloc.',
    {
      h: [
        ['Complétion', 'Basée sur événements player'],
        ['Statuts', '3 états principaux'],
        ['Blocs', 'Sessions dans la période'],
      ],
      st: [
        ['Lire le camembert statuts', 'Un gros « non commencé » après invitation = problème de communication ou de liens.'],
        ['Analyser la ligne complétion', 'Moyenne par créneau temporel parmi les actifs — zéro peut signifier peu d’activité sur la période.'],
        ['Prioriser les blocs', 'Les blocs avec peu d’apprenants distincts peuvent être trop difficiles ou mal placés dans le parcours.'],
      ],
      se: [{ hd: 'Limites', p: ['Sans session complétée, le temps d’engagement sous-estime l’effort — interprétez avec prudence.'] }],
      t: ['Exportez périodiquement vers Excel pour archives si votre instance le permet.'],
      r: ['analytics-exports', 'demarrage-tableau-bord'],
    },
  ),

  'analytics-exports': d(
    'Les exports servent au reporting RH, financier ou qualité : tableaux de progression, listes d’inscrits, preuves de suivi.',
    {
      h: [
        ['Formats', 'CSV / XLS selon modules'],
        ['Périmètre', 'Filtres appliqués'],
        ['Fréquence', 'Mensuelle recommandée'],
      ],
      st: [
        ['Préparer l’export', 'Appliquez les filtres (dates, formation) avant export pour un fichier exploitable.'],
        ['Pseudonymiser', 'Si vous partagez à un tiers, retirez les colonnes directement identifiantes quand possible.'],
        ['Archiver', 'Stockez sur un référentiel sécurisé avec politique de rétention alignée RGPD.'],
      ],
      se: [{ hd: 'Qualiopi', p: ['Conservez les exports signés ou horodatés comme preuve d’activité pour les audits.'] }],
      t: ['Automatisez les exports récurrents si une API ou un connecteur est disponible sur votre offre.'],
      r: ['donnees-conservation', 'qualite-tracabilite'],
    },
  ),

  'inst-collaborateurs': d(
    'L’administrateur d’établissement crée ou invite des utilisateurs, assigne les rôles (formateur, RH, etc.) et gère les accès aux modules sensibles.',
    {
      h: [
        ['Invitations', 'E-mail + lien'],
        ['Rôles', 'Principe du moindre privilège'],
        ['Audit', 'Qui a publié quoi'],
      ],
      st: [
        ['Inviter', 'Depuis l’espace institution, ajoutez l’e-mail et le rôle ; l’utilisateur complète son profil à la première connexion.'],
        ['Révoquer', 'Désactivez les comptes partants (fin de contrat) pour sécurité et conformité.'],
        ['Former', 'Un court guide interne sur les rôles évite les erreurs de publication.'],
      ],
      t: ['Évitez les comptes partagés « formation@… » : inauditable en cas d’incident.'],
      r: ['demarrage-roles', 'donnees-mots-de-passe'],
    },
  ),

  'inst-structure': d(
    'La structure définit ce qui appartient à l’établissement (tous les parcours des formateurs internes, corbeille commune, charte) versus espaces personnels.',
    {
      h: [
        ['Périmètre', 'Institution vs perso'],
        ['Visibilité', 'Partage inter-formateurs'],
        ['Catalogue', 'Offre globale'],
      ],
      st: [
        ['Cartographier', 'Listez qui produit du contenu et sous quel compte pour clarifier la propriété intellectuelle.'],
        ['Standardiser', 'Modèles de parcours « maison » dupliqués par les formateurs accélèrent le déploiement.'],
        ['Superviser', 'Les admins voient souvent une vue agrégée — utilisez-la pour équilibrer la charge.'],
      ],
      se: [{ hd: 'Multi-sites', p: ['Les structures multi-établissements peuvent avoir des cloisonnements — vérifiez votre contrat.'] }],
      t: ['Documentez la procédure quand un formateur quitte l’organisation (transfert de propriété des parcours).'],
      r: ['inst-trash', 'marque-charte'],
    },
  ),

  'inst-trash': d(
    'La corbeille institutionnelle conserve formations et podcasts supprimés (soft delete) pour restauration ou purge contrôlée.',
    {
      h: [
        ['Restauration', 'Retour en ligne'],
        ['Filtres', 'Par type / date'],
        ['Droits', 'Admin institution'],
      ],
      st: [
        ['Accéder', 'Espace établissement, section corbeille ou équivalent selon votre build.'],
        ['Restaurer', 'Sélectionnez l’élément puis restaurez — vérifiez les dépendances (médias liés).'],
        ['Purger', 'Ne purgez définitivement qu’après validation juridique / archivage externe.'],
      ],
      se: [{ hd: 'RGPD', p: ['La suppression ne supprime pas toujours immédiatement les preuves légales — respectez la durée légale de conservation.'] }],
      t: ['Formez les formateurs : suppression ≠ disparition instantanée ; évitez les fuites de stress.'],
      r: ['donnees-conservation', 'pub-statuts'],
    },
  ),

  'marque-charte': d(
    'La charte institutionnelle (couleurs primaires, logo) se propage souvent au player et aux espaces apprenants pour une expérience cohérente.',
    {
      h: [
        ['Couleurs', 'Variables CSS institution'],
        ['Logo', 'Fichier haute définition'],
        ['Player', 'Rendu temps réel'],
      ],
      st: [
        ['Configurer', 'Dans les paramètres d’établissement / branding, uploadez logo et choisissez la teinte dominante.'],
        ['Tester', 'Ouvrez un parcours en prévisualisation apprenant pour valider contraste et lisibilité.'],
        ['Valider', 'Faites relire par communication interne avant déploiement large.'],
      ],
      se: [{ hd: 'Accessibilité', p: ['Un primaire trop clair sur blanc échoue au WCAG — utilisez les outils de contraste.'] }],
      t: ['Gardez une version « sombre » cohérente si vos apprenants utilisent le mode nuit.'],
      r: ['marque-accessibilite', 'marque-landing'],
    },
  ),

  'marque-accessibilite': d(
    'Lyrra propose un Centre de lecture adaptée (barre d’accessibilité) : contrastes, police OpenDyslexic, masque de lecture, loupe, synthèse vocale sur sélection, et profils Malvoyance / Dys — en plus des bonnes pratiques WCAG/RGAA sur les contenus.',
    {
      h: [
        ['Centre a11y', 'Icône Accessibilité dans l’interface'],
        ['Profils', 'Standard, Malvoyance, Dys (cumulables)'],
        ['Contenu', 'Sous-titres, alt text, titres de blocs'],
      ],
      st: [
        [
          'Ouvrir le centre',
          'Cliquez l’icône Accessibilité (centre de lecture adaptée). Choisissez un profil Malvoyance ou Dys, ou cumulez les réglages.',
        ],
        [
          'Ajuster lecture',
          'Activez contraste renforcé, police de lecture facilitée (OpenDyslexic), interlignage, espacement, masque de lecture ou loupe qui suit le curseur.',
        ],
        [
          'Synthèse vocale',
          'Activez la lecture à voix haute sur sélection pour faire lire un passage dans le player ou l’interface.',
        ],
        [
          'Côté auteur',
          'Dans EduFlow : titres de blocs explicites, textes alternatifs sur images, sous-titres / VTT sur les vidéos, formulations de quiz claires.',
        ],
      ],
      se: [
        {
          hd: 'Ce que le centre ne remplace pas',
          p: [
            'Les réglages sont sauvegardés sur l’appareil de l’utilisateur. Ils n’exemptent pas de produire des contenus accessibles (structure, contrastes de charte, médias sous-titrés).',
            'Sur certains blocs player, un module Dys dédié peut aussi proposer règle de lecture et fond coloré.',
          ],
        },
      ],
      t: [
        'Testez un parcours au clavier seul (Tab / Entrée) après activation du focus renforcé.',
        'Vérifiez le contraste de votre couleur primaire institutionnelle : un primaire trop clair sur blanc échoue souvent au critère AA.',
      ],
      r: ['marque-charte', 'medias-video', 'eduflow-blocs'],
    },
  ),

  'marque-landing': d(
    'Les pages publiques ou vitrine présentent votre catalogue et votre valeur aux visiteurs externes lorsque le module est activé.',
    {
      h: [
        ['SEO', 'Titres & meta'],
        ['Contenu', 'Offre claire'],
        ['Conversion', 'Appel à l’action inscription'],
      ],
      st: [
        ['Rédiger', 'Mettez en avant différenciateurs, publics cibles et preuves sociales autorisées.'],
        ['Relier au catalogue', 'Les fiches formation doivent être à jour pour éviter les promesses fausses.'],
        ['Mesurer', 'Suivez trafic et taux de conversion vers inscription ou contact.'],
      ],
      se: [{ hd: 'Mentions légales', p: ['Éditeur, hébergeur, politique de confidentialité doivent être accessibles depuis le footer.'] }],
      t: ['Alignez les visuels avec la même charte que le player pour une reconnaissance de marque.'],
      r: ['pub-catalogue', 'marque-charte'],
    },
  ),

  'qualite-qualiopi': d(
    'Les espaces Qualiopi aident à préparer indicateurs, preuves et auto-positionnement par critère ; le détail dépend du paramétrage de votre instance.',
    {
      h: [
        ['Critères', 'Indicateurs & preuves'],
        ['Preuves', 'Exports, objectifs, évaluations'],
        ['Espace', 'Conformité / Qualiopi (si activé)'],
      ],
      st: [
        [
          'Ouvrir l’espace conformité',
          'Depuis le menu établissement / RH (selon activation), ouvrez l’espace Qualiopi ou conformité pédagogique dédié.',
        ],
        [
          'Renseigner les critères',
          'Pour chaque indicateur, indiquez statut, actions et échéances ; joignez exports Lyrra (progression, certificats, analytics).',
        ],
        [
          'Croiser avec EduFlow',
          'Reliez objectifs pédagogiques, évaluations et traces d’assiduité comme preuves d’efficacité.',
        ],
        [
          'Simuler l’audit',
          'Un observateur externe doit retrouver chaque preuve en moins de 10 minutes sans votre aide.',
        ],
      ],
      se: [
        {
          hd: 'Responsabilité',
          p: [
            'Lyrra facilite la collecte et la traçabilité ; la conformité Qualiopi reste sous la responsabilité de l’organisme de formation.',
            'Il n’y a plus de module « veille réglementaire » dans le menu : documentez votre veille hors plateforme si votre certificateur l’exige.',
          ],
        },
      ],
      t: ['Exportez un snapshot des tableaux de bord avant chaque audit pour figer les chiffres.'],
      r: ['qualite-tracabilite', 'eduflow-objectifs'],
    },
  ),

  'qualite-tracabilite': d(
    'La traçabilité relie objectifs, contenus vus, temps passé, évaluations et décisions (certification, exclusion) pour reconstituer le parcours d’un apprenant.',
    {
      h: [
        ['Journal', 'Événements horodatés'],
        ['Identité', 'Lien compte / participant'],
        ['Conservation', 'Durée légale'],
      ],
      st: [
        [
          'Configurer',
          'Assurez-vous que chaque bloc critique (évaluation, attestation) logue une trace.',
        ],
        [
          'Contrôler',
          'En cas de litige, extrayez la chronologie complète pour le juridique.',
        ],
        [
          'Archiver',
          'Ne détruisez pas avant la fin des délais prescription ou obligations Qualiopi.',
        ],
      ],
      se: [{ hd: 'Anonymisation', p: ['Après délai, l’anonymisation peut être possible pour les données non nécessaires.'] }],
      t: ['Documentez la correspondance entre identifiant Lyrra et dossier papier si coexistence.'],
      r: ['donnees-conservation', 'app-certificats'],
    },
  ),

  'donnees-rgpd': d(
    'Lyrra traite des données personnelles (identité, progression, parfois biométrie ou e-mails métier) en tant qu’outil ; vous êtes responsable du traitement pour votre organisme.',
    {
      h: [
        ['Rôles', 'Responsable vs sous-traitant'],
        ['Finalités', 'Formation, certification, RH'],
        ['Base légale', 'Contrat, obligation légale, consentement'],
      ],
      st: [
        [
          'Tenir le registre',
          'Listez traitements Lyrra : quelles données, qui accède, durée, sécurité.',
        ],
        [
          'Informer',
          'Les apprenants doivent avoir une politique de confidentialité accessible avant collecte.',
        ],
        [
          'Répondre aux droits',
          'Accès, rectification, effacement, limitation — délais réglementaires.',
        ],
      ],
      se: [{ hd: 'Transferts hors UE', p: ['Si des sous-traitants sont US-based, vérifiez clauses types ou décision d’adéquation.'] }],
      t: ['Ne mélangez pas données pros et persos dans un même export sans base légale claire.'],
      r: ['donnees-conservation', 'inst-collaborateurs'],
    },
  ),

  'donnees-conservation': d(
    'Les durées de conservation varient : preuves de formation souvent plusieurs années après fin de session ; données de prospection plus courtes.',
    {
      h: [
        ['Actif', 'Pendant la relation de formation'],
        ['Archivage', 'Accès restreint'],
        ['Destruction', 'Procédure sécurisée'],
      ],
      st: [
        [
          'Définir la politique',
          'Validez avec votre DPO / juridique des durées par type de donnée.',
        ],
        [
          'Configurer',
          'Adaptez workflows internes (exports annuels puis purge des brouillons).',
        ],
        ['Prouver', 'En audit, montrez la procédure même si la technique est manuelle.'],
      ],
      se: [{ hd: 'Preuves', p: ['Certaines données doivent être conservées même si l’apprenant demande effacement — informez-le.'] }],
      t: ['Ne conservez pas indéfiniment les comptes inactifs sans relance — principe de minimisation.'],
      r: ['inst-trash', 'donnees-rgpd'],
    },
  ),

  'donnees-mots-de-passe': d(
    'Les mots de passe doivent être robustes et personnels ; l’administrateur de l’établissement gère les comptes.',
    {
      h: [
        ['Politique', 'Longueur, complexité'],
        ['MFA', 'Si activé par l’IT'],
        ['Sessions', 'Expiration auto'],
      ],
      st: [
        ['Choisir', 'Phrase de passe longue > mot court complexe oublié.'],
        ['Ne pas réutiliser', 'Un leak sur un autre site ne doit pas compromettre Lyrra.'],
        ['Signaler', 'Toute session suspecte à l’administrateur.'],
      ],
      se: [{ hd: 'Comptes service', p: ['Les comptes techniques doivent avoir rotation de secrets documentée.'] }],
      t: ['Activez la déconnexion sur les postes partagés après chaque session.'],
      r: ['support-faq', 'demarrage-profil'],
    },
  ),

  'support-faq': d(
    'Problèmes fréquents : média qui ne charge pas (format, CORS, URL expirée), parcours qui ne se publie pas (erreurs validation), apprenant bloqué (cache navigateur).',
    {
      h: [
        ['Étape 1', 'Reproduire en navigation privée'],
        ['Étape 2', 'Autre navigateur'],
        ['Étape 3', 'Capturer erreur console si possible'],
      ],
      st: [
        [
          'Média',
          'Vérifiez que le fichier existe encore et que l’URL signée n’a pas expiré.',
        ],
        ['Publication', 'Ouvrez Validation et corrigez les erreurs rouges.'],
        ['Player blanc', 'Désactivez extensions adblock sur le domaine Lyrra.'],
      ],
      se: [{ hd: 'Escale', p: ['Préparez ID parcours, ID apprenant (hash), heure UTC, capture d’écran.'] }],
      t: ['Le rafraîchissement forcé (Ctrl+F5) résout souvent les assets JS en cache après déploiement.'],
      r: ['support-navigateur', 'support-contact'],
    },
  ),

  'support-navigateur': d(
    'Lyrra cible les navigateurs modernes (Chrome, Firefox, Safari, Edge récents). Internet Explorer n’est pas supporté.',
    {
      h: [
        ['Version', 'Dernière maj stable'],
        ['Cookies', 'Nécessaires pour rester connecté'],
        ['WebGL', 'Rare pour canvas designer'],
      ],
      st: [
        ['Mettre à jour', 'Appliquez les correctifs sécurité du navigateur.'],
        ['Autoriser', 'Cookies et stockage local pour rester connecté.'],
        [
          'Tester mobile',
          'Safari iOS peut avoir des limites lecture média — testez sur device réel.',
        ],
      ],
      se: [{ hd: 'Réseau', p: ['Proxys d’entreprise peuvent bloquer WebSocket ou uploads — contacter l’IT.'] }],
      t: ['Désactivez les VPN expirimentaux si uploads échouent systématiquement.'],
      r: ['support-faq', 'medias-video'],
    },
  ),

  'support-contact': d(
    'Le support Lyrra (ou votre intégrateur) traite les bugs et questions contractuelles ; votre admin interne traite les comptes et rôles.',
    {
      h: [
        ['Niveau 1', 'Admin établissement'],
        ['Niveau 2', 'Éditeur Lyrra'],
        ['SLA', 'Selon contrat'],
      ],
      st: [
        ['Qualifier', 'Fonctionnel vs technique vs demande d’évolution.'],
        [
          'Documenter',
          'Pas à pas, captures, URL, heure, utilisateur impacté (sans données sensibles en clair dans le titre).',
        ],
        ['Suivre', 'Numéro de ticket ; relancez avec éléments nouveaux seulement.'],
      ],
      se: [{ hd: 'Urgences', p: ['Perte de données ou sécurité : canal prioritaire défini au contrat.'] }],
      t: ['Un ticket « ça marche pas » sans contexte est relégué en fin de file — soyez précis.'],
      r: ['support-faq', 'donnees-rgpd'],
    },
  ),

  'api-rest-openapi': d(
    'Chaque instance Lyrra expose une documentation HTTP interactive (Swagger UI) et un fichier OpenAPI JSON, générés à partir du code backend.',
    {
      h: [
        ['Swagger UI', API_DOC_PATHS.swagger],
        ['OpenAPI JSON', API_DOC_PATHS.openapiJson],
        ['Santé', API_DOC_PATHS.health],
      ],
      st: [
        [
          'Ouvrir Swagger',
          `Dans le navigateur : même origine que l’application + ${API_DOC_PATHS.swagger}. Vous y voyez chaque méthode (GET, POST, …) et chemin exact.`,
        ],
        [
          'Authentification',
          'La plupart des routes exigent un cookie de session ou un en-tête Bearer. Swagger peut envoyer des requêtes si vous configurez les en-têtes ou les cookies dans le navigateur.',
        ],
        [
          'Préfixes principaux',
          'Les modules REST sont regroupés par préfixe (auth, eduflows, institution, v2, …). La liste à jour est dans l’onglet Documentation du panneau MCP institution.',
        ],
      ],
      se: [
        {
          hd: 'Préfixes V1 (extraits)',
          p: [
            `La plateforme compte ${API_V1_ROUTE_MODULES.length} préfixes V1 documentés dans l’app (ex. /api/eduflows pour les parcours, /api/auth pour la connexion).`,
          ],
          b: API_V1_ROUTE_MODULES.slice(0, 12).map((m) => `${m.prefix} — ${m.description}`),
        },
        {
          hd: 'API V2',
          p: [
            `Toutes les routes métier V2 sont sous /api/v2 (actuellement ${API_V2_ROUTE_MODULES.length} sous-modules : campagnes, catalogue, intégrations, etc.).`,
          ],
          b: API_V2_ROUTE_MODULES.map((m) => `${m.prefix} — ${m.description}`),
        },
      ],
      t: [
        'En cas de doute sur un chemin, Swagger fait foi : il est régénéré depuis les routeurs Express.',
      ],
      r: ['mcp-lyrra', 'donnees-rgpd'],
    },
  ),

  'mcp-lyrra': d(
    'Le Model Context Protocol permet à un client IA (Claude Desktop, Cursor, etc.) d’appeler des outils qui dialoguent avec Lyrra via l’API HTTP, en utilisant des clés API.',
    {
      h: [
        ['Serveur MCP', 'Dépôt : lyrra-studio-app/apps/mcp-server (stdio, Node 20+)'],
        ['Auth clé API', 'POST /api/auth/api-key/token → JWT pour les appels'],
        ['Base API', '/api'],
      ],
      st: [
        [
          'Clés institution',
          'Dans Établissement → MCP → Clés API : création de paires pour vos concepteurs. Endpoints REST : lister les routes ci-dessous.',
        ],
        [
          'Clés personnelles',
          'Dans Paramètres → Clés API (MCP & API) : clés rattachées à votre compte utilisateur.',
        ],
        [
          'Variables d’environnement',
          'LYRRA_API_URL (…/api), LYRRA_CLIENT_ID + LYRRA_CLIENT_SECRET ou LYRRA_ACCESS_TOKEN (JWT). Optionnel : LYRRA_OPENAPI_URL, LYRRA_MCP_MAX_TOOLS.',
        ],
      ],
      se: [
        {
          hd: 'Catalogue in-app',
          p: [
            'Établissement → MCP → Documentation : la liste exhaustive correspond à chaque opération HTTP du backend (OpenAPI). Une ligne = une méthode + un chemin ; le nom d’outil MCP recommandé est l’`operationId` ; la description longue inclut le fichier routeur et des indications d’authentification.',
          ],
        },
        {
          hd: 'Endpoints REST — clés institution',
          p: ['Utilisés par le tableau de bord institution pour gérer les identifiants MCP.'],
          b: [
            MCP_HTTP_ENDPOINTS.institutionKeys.list,
            MCP_HTTP_ENDPOINTS.institutionKeys.create,
            MCP_HTTP_ENDPOINTS.institutionKeys.updateName,
            MCP_HTTP_ENDPOINTS.institutionKeys.toggle,
            MCP_HTTP_ENDPOINTS.institutionKeys.remove,
          ],
        },
        {
          hd: 'Endpoints REST — clés personnelles',
          p: ['Compte concepteur (paramètres).'],
          b: [
            MCP_HTTP_ENDPOINTS.userKeys.list,
            MCP_HTTP_ENDPOINTS.userKeys.create,
            MCP_HTTP_ENDPOINTS.userKeys.toggle,
            MCP_HTTP_ENDPOINTS.userKeys.remove,
          ],
        },
      ],
      t: [
        'Claude Desktop : utilisez `command: npx` et `args: ["-y", "@lyrra/mcp-server"]` avec les variables d’environnement (voir Établissement → MCP). Développeurs du monorepo : alternative locale avec `node` et le chemin absolu vers `apps/mcp-server/dist/index.js` après `npm run build`.',
      ],
      r: ['api-rest-openapi', 'inst-collaborateurs'],
    },
  ),
};
